import type { ApiClient } from '../api/client.js';
import {
  mediaListSchema,
  mediaSchema,
  publicMediaListSchema,
  readUrlSchema,
  uploadTicketsSchema,
  type Media,
  type MediaVariant,
  type PublicMedia,
  type ReadUrl,
  type UploadTicket,
} from './media-contract.js';

/**
 * Every media call the app makes, through the one API client the shell provides — so each gets
 * credentials, the transparent refresh-and-retry, and response validation without this module
 * knowing anything about any of it.
 *
 * The single exception is `putToStorage`, and it is not an exception to the rule so much as a
 * different destination: that request goes to Cloudflare R2, not to our API. It must NOT carry
 * our cookies, it is authorised by the presigned URL alone, and it needs upload progress — which
 * `fetch` cannot report. Hence XMLHttpRequest, the only browser API that can say how far a
 * request body has got.
 */

export interface DeclaredVariant {
  bytes: number;
  contentType: string;
  width: number;
  height: number;
}

export interface UploadDeclaration {
  clientId: string;
  originalFilename: string;
  originalBytes?: number;
  full: DeclaredVariant;
  thumb: DeclaredVariant;
}

export interface CreateTicketsInput {
  entityType: string;
  entityId?: string | null;
  kind?: string;
  visibility?: 'internal' | 'customer_visible';
  /**
   * Files an upload into a gallery folder on arrival, so it does not land unfiled and need a
   * second move. Optional, and every existing caller omits it — only the gallery ever sets it.
   */
  folderId?: string | null;
  files: UploadDeclaration[];
}

export async function createUploadTickets(
  client: ApiClient,
  input: CreateTicketsInput,
): Promise<UploadTicket[]> {
  const { tickets } = await client.post('/media/uploads', input, { schema: uploadTicketsSchema });
  return tickets;
}

export async function confirmUpload(client: ApiClient, mediaId: string): Promise<Media> {
  return await client.post(`/media/uploads/${mediaId}/confirm`, undefined, { schema: mediaSchema });
}

export async function listMedia(
  client: ApiClient,
  query: { entityType: string; entityId?: string | null; kind?: string },
): Promise<Media[]> {
  const { items } = await client.get('/media', {
    schema: mediaListSchema,
    query: {
      entityType: query.entityType,
      ...(query.entityId ? { entityId: query.entityId } : {}),
      ...(query.kind ? { kind: query.kind } : {}),
    },
  });
  return items;
}

/** The full-size URL, fetched only when the lightbox opens. */
export async function getReadUrl(
  client: ApiClient,
  mediaId: string,
  variant: MediaVariant = 'full',
): Promise<ReadUrl> {
  return await client.get(`/media/${mediaId}/url`, { schema: readUrlSchema, query: { variant } });
}

export async function deleteMedia(client: ApiClient, mediaId: string): Promise<void> {
  await client.del(`/media/${mediaId}`);
}

export async function updateMedia(
  client: ApiClient,
  mediaId: string,
  patch: { caption?: string | null; visibility?: 'internal' | 'customer_visible'; kind?: string },
): Promise<Media> {
  return await client.patch(`/media/${mediaId}`, patch, { schema: mediaSchema });
}

export async function reorderMedia(
  client: ApiClient,
  input: { entityType: string; entityId?: string | null; order: string[] },
): Promise<Media[]> {
  const { items } = await client.patch('/media/order', input, { schema: mediaListSchema });
  return items;
}

export async function attachMedia(
  client: ApiClient,
  input: { entityType: string; entityId: string; mediaIds: string[] },
): Promise<Media[]> {
  const { items } = await client.post('/media/attach', input, { schema: mediaListSchema });
  return items;
}

/** The public read path — no session, just the grant token (task 11's media grants). */
export async function listPublicMedia(client: ApiClient, grant: string): Promise<PublicMedia[]> {
  const { items } = await client.get('/media/public', {
    schema: publicMediaListSchema,
    query: { grant },
  });
  return items;
}

export async function getPublicReadUrl(
  client: ApiClient,
  grant: string,
  mediaId: string,
  variant: MediaVariant = 'full',
): Promise<ReadUrl> {
  return await client.get(`/media/public/${mediaId}/url`, {
    schema: readUrlSchema,
    query: { grant, variant },
  });
}

// --- The direct-to-storage upload -------------------------------------------------------------

/** Raised when the bytes did not reach the bucket. Retryable unless the URL was refused. */
export class StorageUploadError extends Error {
  readonly status: number | undefined;
  /** True when trying again could plausibly work: a dropped connection, a timeout, a 5xx. */
  readonly retryable: boolean;

  constructor(message: string, status: number | undefined, retryable: boolean) {
    super(message);
    this.name = 'StorageUploadError';
    this.status = status;
    this.retryable = retryable;
  }
}

export interface PutToStorageInput {
  url: string;
  body: Blob;
  headers: Record<string, string>;
  /** Fraction 0–1. Called often enough for a progress bar to look alive on a slow link. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/** Test seam: hand in a stub instead of the browser's XMLHttpRequest. */
export type XhrFactory = () => XMLHttpRequest;

export function putToStorage(
  input: PutToStorageInput,
  createXhr: XhrFactory = () => new XMLHttpRequest(),
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = createXhr();
    xhr.open('PUT', input.url, true);

    for (const [name, value] of Object.entries(input.headers)) {
      // Content-Length is set by the browser from the body and may not be set by hand; the
      // presigned URL already commits to it, and the browser will send the matching value.
      if (name.toLowerCase() === 'content-length') continue;
      xhr.setRequestHeader(name, value);
    }

    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (event.lengthComputable && event.total > 0) {
        input.onProgress?.(Math.min(1, event.loaded / event.total));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        input.onProgress?.(1);
        resolve();
        return;
      }
      // 403 is an expired or malformed URL: retrying the same URL is pointless, but asking for
      // a fresh ticket is not — which is what the uploader does.
      reject(
        new StorageUploadError(
          `The image could not be uploaded (${xhr.status})`,
          xhr.status,
          xhr.status >= 500 || xhr.status === 429,
        ),
      );
    };

    // The two ways a 4G connection fails mid-upload, and the case where the user cancelled.
    xhr.onerror = () => reject(new StorageUploadError('The connection was lost', undefined, true));
    xhr.ontimeout = () => reject(new StorageUploadError('The upload timed out', undefined, true));
    xhr.onabort = () =>
      reject(new StorageUploadError('The upload was cancelled', undefined, false));

    input.signal?.addEventListener('abort', () => xhr.abort(), { once: true });

    xhr.send(input.body);
  });
}
